import React, {
    useState,
    useRef,
    MutableRefObject,
    useLayoutEffect,
} from 'react';
import classnames from 'classnames/bind';
import { Editor } from '@tinymce/tinymce-react';

import COLOR from '@CONSTANTS/COLOR.constant';
import PDF from '@CONSTANTS/PDF.constant';
import { useGetAppSettingsQuery, useGetSystemSettingsQuery } from '@SERVICES';

import getEditorConfig from '@HELPERS/getEditorConfig';
import { useTinyMCE } from '@HOOKS/useTinyMCE';

import CircularProgressIndicator from '@COMPONENTS/SHARED/CircularProgressIndicator';

import { EditorStyleContent } from '@CONSTANTS/EDITOR_STYPE';
import styles from './IntroductionTextEditor.module.scss';

const cx: CX = classnames.bind(styles);

const PAGE_WIDTH = PDF['pdf-page-width'];

interface Props {
    initialValue: string;
    onEditorChange: (content: string) => void;
}

function IntroductionTextEditor(props: Props) {
    const { initialValue, onEditorChange } = props;

    // Get app settings for dynamic colors and system settings for WYSIWYG configuration
    const { data: appSettings } = useGetAppSettingsQuery();
    const { data: systemSettings } = useGetSystemSettingsQuery();
    
    // Use the TinyMCE hook to ensure scripts are loaded
    const { isLoaded: isTinyMCELoaded, isLoading: isTinyMCELoading, error: tinyMCEError } = useTinyMCE();

    const editorConfig = getEditorConfig('portrait', appSettings, systemSettings);

    const editorRef: MutableRefObject<any> = useRef(null);

    const [isEditorReady, setEditorReady] = useState<boolean>(false);

    useLayoutEffect(() => {
        if (isEditorReady) {
            const width = PAGE_WIDTH;

            editorRef.current.contentAreaContainer.style.width = `${width}px`;
            editorRef.current.contentAreaContainer.style.maxWidth = `${width}px`;
        }
    }, [isEditorReady]);

    const cleanFontSizes = (content: string) => content.replace(/font-size:\s*(\d+)pt;/g, (match, size) => {
        const pxSize = `${parseInt(size, 10)}px`; // Convert pt to px
        return `font-size: ${pxSize};`;
    });

    const cleanedContent = cleanFontSizes(initialValue);

    // Show loading if TinyMCE is not ready yet
    if (isTinyMCELoading || !isTinyMCELoaded) {
        return (
            <div className={cx('editor-loading')}>
                <CircularProgressIndicator
                    color={COLOR['marriott-primary']}
                    size={40}
                    thickness={4}
                />
                <div style={{ marginTop: '10px', textAlign: 'center' }}>
                    {isTinyMCELoading ? 'Loading editor...' : 'Initializing editor...'}
                </div>
            </div>
        );
    }

    // Show error if TinyMCE failed to load
    if (tinyMCEError) {
        return (
            <div className={cx('editor-error')} style={{ padding: '20px', textAlign: 'center' }}>
                <div style={{ color: 'red', marginBottom: '10px' }}>
                    Editor Load Error
                </div>
                <div style={{ fontSize: '14px' }}>
                    {tinyMCEError}
                </div>
            </div>
        );
    }

    return (
        <div
            className={cx('introduction-text-editor', {
                show: isEditorReady,
            })}
        >
            <Editor
                onInit={(evt, editor) => {
                    editorRef.current = editor;
                    setEditorReady(true);
                    onEditorChange(editor.getContent());
                }}
                initialValue={cleanedContent}
                onEditorChange={(content) => {
                    onEditorChange(content || '');
                }}
                init={{
                    ...editorConfig,
                    
                    // Modal-specific configuration to fix menu issues
                    promotion: false,
                    
                    // Increase z-index for modal compatibility
                    skin_url: undefined, // Use default skin
                    content_css: [
                        ...editorConfig.content_css,
                        `data:text/css;charset=utf-8,${encodeURIComponent(`
                            .tox-tinymce-aux { z-index: 10001 !important; }
                            .tox-menu { z-index: 10001 !important; }
                            .tox-silver-sink { z-index: 10001 !important; }
                            .tox-dialog-wrap { z-index: 10002 !important; }
                            .tox-dialog { z-index: 10002 !important; }
                        `)}`,
                    ],

                    height: 500,
                    min_height: 500,
                    toolbar: [
                        'undo redo | styleselect | bold italic underline strikethrough | fontsize blocks fontfamily | alignleft aligncenter alignright alignjustify | outdent indent | numlist bullist',
                        'forecolor backcolor removeformat | charmap emoticons | preview print | image media template link anchor | ltr rtl',
                    ],
                    setup: () => {
                        const style = document.createElement('style');
                        style.textContent = `${EditorStyleContent}
                            .tox-tinymce-aux { z-index: 10001 !important; }
                            .tox-menu { z-index: 10001 !important; }
                            .tox-silver-sink { z-index: 10001 !important; }
                            .tox-dialog-wrap { z-index: 10002 !important; }
                            .tox-dialog { z-index: 10002 !important; }
                        `;
                        document.head.appendChild(style);
                    },
                }}
            />
        </div>
    );
}

export default IntroductionTextEditor; 